import React, { useRef, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { searchPartyMusic } from '../services/partySearch';
import {
  catalogResultToQueueTrack,
  isCatalogResultQueueable,
} from '../services/catalogQueue';

export function closeSearchResultsState() {
  return {
    catalogResults: [],
    myMusicResults: [],
    status: 'idle',
    error: null,
    emptyMessage: null,
  };
}

function ResultRow({
  title,
  thumbnail,
  disabled,
  disabledLabel,
  onSelect,
  testId,
}) {
  const content = (
    <>
      {thumbnail ? (
        <img
          src={thumbnail}
          alt=""
          className="h-16 w-24 shrink-0 rounded object-cover sm:h-20 sm:w-28"
        />
      ) : (
        <div className="h-16 w-24 shrink-0 rounded bg-white/10 sm:h-20 sm:w-28" />
      )}
      <div className="min-w-0 flex-1">
        <h3 className="line-clamp-2 font-semibold text-white">{title}</h3>
        {disabledLabel ? (
          <p className="mt-1 text-sm text-white/60">{disabledLabel}</p>
        ) : null}
      </div>
    </>
  );

  if (disabled) {
    return (
      <div
        data-testid={testId}
        className="flex min-w-0 cursor-not-allowed gap-3 rounded-lg border border-white/10 p-3 opacity-70 sm:gap-4 sm:p-4"
      >
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onSelect}
      className="flex min-w-0 gap-3 rounded-lg border border-white/15 p-3 text-left transition-colors hover:border-blue-400 sm:gap-4 sm:p-4"
    >
      {content}
    </button>
  );
}

const SearchBar = ({ onResultSelect }) => {
  const [query, setQuery] = useState('');
  const [catalogResults, setCatalogResults] = useState([]);
  const [myMusicResults, setMyMusicResults] = useState([]);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [emptyMessage, setEmptyMessage] = useState(null);
  const inFlightRef = useRef(false);
  const requestSeqRef = useRef(0);

  const handleSearch = async (event) => {
    if (event) event.preventDefault();
    const rawQuery = query.trim();
    if (!rawQuery || inFlightRef.current) return;

    inFlightRef.current = true;
    const seq = requestSeqRef.current + 1;
    requestSeqRef.current = seq;
    setStatus('loading');
    setError(null);
    setEmptyMessage(null);

    try {
      const response = await searchPartyMusic(rawQuery);
      if (seq !== requestSeqRef.current) return;
      setCatalogResults(response.catalogResults || []);
      setMyMusicResults(response.myMusicResults || []);
      setStatus(response.status || 'empty');
      setError(response.error || null);
      setEmptyMessage(response.emptyMessage || null);
    } catch {
      if (seq !== requestSeqRef.current) return;
      setCatalogResults([]);
      setMyMusicResults([]);
      setStatus('unavailable');
      setError('Search is temporarily unavailable. Try again.');
      setEmptyMessage(null);
    } finally {
      if (seq === requestSeqRef.current) {
        inFlightRef.current = false;
      }
    }
  };

  const closeSearchResults = () => {
    requestSeqRef.current += 1;
    inFlightRef.current = false;
    const next = closeSearchResultsState();
    setCatalogResults(next.catalogResults);
    setMyMusicResults(next.myMusicResults);
    setStatus(next.status);
    setError(next.error);
    setEmptyMessage(next.emptyMessage);
    if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)')?.matches) {
      window.scrollTo(0, 0);
    }
  };

  const selectCatalog = (result) => {
    const track = catalogResultToQueueTrack(result);
    if (!track) return;
    onResultSelect(track);
  };

  const selectMyMusic = (result) => {
    if (!result?.actions?.queue || !result.queueTrack) return;
    onResultSelect(result.queueTrack);
  };

  const loading = status === 'loading';
  const hasResults = catalogResults.length > 0 || myMusicResults.length > 0;
  const showPanel = hasResults || status === 'empty' || status === 'unavailable' || Boolean(error);

  return (
    <div className="w-full min-w-0 max-w-4xl mx-auto p-2 sm:p-4">
      {showPanel && (
        <div
          className="sticky z-30 mb-3 flex items-center justify-between gap-2 rounded-lg border border-white/10 bg-black/85 px-2 py-1 backdrop-blur-md md:static"
          style={{ top: 'max(0.5rem, env(safe-area-inset-top))' }}
        >
          <p className="min-w-0 truncate px-2 text-sm text-white/80">Search results</p>
          <button
            type="button"
            onClick={closeSearchResults}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-white hover:bg-white/10"
            aria-label="Close search"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
      )}

      <form onSubmit={handleSearch} className="mb-6 flex min-w-0 gap-2">
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search for music..."
          className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-black focus:border-blue-500 focus:outline-none sm:px-4"
        />
        <button
          type="submit"
          disabled={loading}
          className="shrink-0 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700 disabled:opacity-50 sm:px-6"
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </form>

      {error && (
        <div role="alert" className="mb-4 text-red-400">
          {error}
        </div>
      )}

      {status === 'empty' && emptyMessage && (
        <p role="status" className="mb-4 text-white/70">
          {emptyMessage}
        </p>
      )}

      <div className="space-y-6">
        {myMusicResults.length > 0 && (
          <section aria-label="My Music">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-white/70">
              My Music
            </h2>
            <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2">
              {myMusicResults.map((result) => (
                <ResultRow
                  key={result.resultId}
                  testId={`my-music-result-${result.resultId}`}
                  title={result.displayTitle}
                  thumbnail={result.thumbnail}
                  disabled={!result.actions?.queue}
                  disabledLabel={result.actions?.queue ? null : 'Not available yet'}
                  onSelect={() => selectMyMusic(result)}
                />
              ))}
            </div>
          </section>
        )}

        {catalogResults.length > 0 && (
          <section aria-label="Rhythmé Catalog">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-white/70">
              Rhythmé Catalog
            </h2>
            <div className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2">
              {catalogResults.map((result) => {
                const queueable = isCatalogResultQueueable(result);
                return (
                  <ResultRow
                    key={result.resultId || result.recordingId}
                    testId={`catalog-result-${result.recordingId}`}
                    title={result.displayTitle}
                    thumbnail={catalogResultToQueueTrack(result)?.thumbnail || result.thumbnail}
                    disabled={!queueable}
                    disabledLabel={queueable ? null : 'Not available yet'}
                    onSelect={() => selectCatalog(result)}
                  />
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default SearchBar;
